import React from 'react';
import { observer } from 'mobx-react';
import { Checkbox, Col, Row } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox/Checkbox';
import {
    Catcher,
    FilterDateMonth,
    FilterDateRange,
    FilterLazyLoadingSelect,
    FilterOriginalUnitTypes,
    FilterPageSize,
    FilterSearch,
    FilterSelect,
    ReloadButton,
    cardLayoutOptions,
    rowGutter,
} from '../';
import cx from 'classnames';
import dayjs from 'dayjs';
import {
    AppSystem,
    IDateRange,
    IDivMainProps,
    IFilterParams,
    IFilterSelect,
    IFilterSelectComposed,
    IGetApiType,
} from '../../interfaces';
import {
    useAppSystem,
    useBillingCompaniesSelect, // useUiSource,
    useCardSetsSelect,
    useCardsSelectWithTotal,
    useCompaniesSelect,
    useCompanyTypesSelect,
    useDiameterSessionsSelect,
    useEntitySelect,
    useOperatorInteractionsCommandsSelect,
    useOperatorInteractionsFilesSelect,
    useOperatorInteractionsGetewaysSelect,
    useOperatorsSelect,
    usePackagesSelect,
    usePaymentTypesSelect,
    useProfileIsRight,
    useRatesImportTypes,
    useTariffPackagesSelectWithTotal,
    useTariffsSelectWithTotal,
    useUiBuyerId,
    useUiCardId,
    useUiCardSetId,
    useUiCompanyId,
    useUiCompanyTypeId,
    useUiDateRange,
    useUiEntityId,
    useUiImportType,
    useUiIsActiveThisMonth,
    useUiIsDelete,
    useUiIsEimProvided,
    useUiOperatorId,
    useUiOperatorInteractionsCommandId,
    useUiOperatorInteractionsGatewayId,
    useUiOperatorInteractionsUploadFileId,
    useUiPackageId,
    useUiPaymentType,
    useUiSearch,
    useUiSellerId,
    useUiSessionId,
    useUiStatus,
    useUiStatusesList,
    useUiTariffId,
    useUiTariffPackageId,
    useUiUserId,
    useUiWithDeleted,
    useUiZoneId,
    useUsersSelectWithTotal,
    useZonesSelect,
} from '../../hooks';
import {
    getCardsSelect,
    getTariffsPackageSelect,
    getTariffsSelect,
    getUsersSelect,
    setUiBuyerId,
    setUiCardId, // setUiSource,
    setUiCardSetId,
    setUiCommandId,
    setUiCompanyId,
    setUiCompanyType,
    setUiDate,
    setUiDateRange,
    setUiEntityId,
    setUiFileId,
    setUiForcedUpdateTime,
    setUiGatewayId,
    setUiImportType,
    setUiIsActiveThisMonth,
    setUiIsDelete,
    setUiIsEimProvided,
    setUiOperatorId,
    setUiPackageId,
    setUiPaymentType,
    setUiSearch,
    setUiSellerId,
    setUiSessionId,
    setUiStatus,
    setUiTariffId,
    setUiTariffPackageId,
    setUiUnitTypes,
    setUiUserId,
    setUiWithDeleted,
    setUiZoneId,
} from '../../middleware';
import { tF, tI } from '../../translate';
import '../../customStyle/input.less';
import Styles from './m_styles.less';

interface IFilterToolbarProps extends IDivMainProps {
    BottomBar?: React.FC;
    filterParams?: IFilterParams;
    idSpinners?: IGetApiType[];
    isReloadButton?: boolean;
    months?: number;
    onChange?: () => void;
}

export const FilterToolbar: React.FC<IFilterToolbarProps> = observer(
    ({
        BottomBar,
        className,
        filterParams,
        idSpinners,
        isReloadButton,
        months,
        onChange = setUiForcedUpdateTime,
        ...props
    }: IFilterToolbarProps) => {
        const appSystem = useAppSystem();
        const isBillingSystem = appSystem === AppSystem.BILLING;

        // const source = useUiSource();
        const buyerId = useUiBuyerId();
        const cardSetId = useUiCardSetId();
        const companyId = useUiCompanyId();
        const companyTypeId = useUiCompanyTypeId();
        const dateRange = useUiDateRange();
        const entityId = useUiEntityId();
        const status = useUiStatus();
        const importType = useUiImportType();
        const isActiveThisMonth = useUiIsActiveThisMonth();
        const isDelete = useUiIsDelete();
        const isEimProvided = useUiIsEimProvided();
        const isLord = useProfileIsRight('LORD');
        const operatorId = useUiOperatorId();
        const operatorInteractionsCommandId = useUiOperatorInteractionsCommandId();
        const operatorInteractionsGetewayId = useUiOperatorInteractionsGatewayId();
        const operatorInteractionsUploadFileId = useUiOperatorInteractionsUploadFileId();
        const packageId = useUiPackageId();
        const paymentType = useUiPaymentType();
        const search = useUiSearch();
        const sellerId = useUiSellerId();
        const sessionId = useUiSessionId();
        const userId = useUiUserId();
        const cardId = useUiCardId();
        const tariffId = useUiTariffId();
        const tariffPackageId = useUiTariffPackageId();
        const withDeleted = useUiWithDeleted();
        const zoneId = useUiZoneId();

        const companiesSelectHook = isBillingSystem ? useBillingCompaniesSelect : useCompaniesSelect;
        const companyTypeSelectHook = useCompanyTypesSelect;
        const entityHook = useEntitySelect;
        const ratesImportTypesHook = useRatesImportTypes;
        const statusHook = useUiStatusesList;
        const operatorInteractionsCommandsHook = useOperatorInteractionsCommandsSelect;
        const operatorInteractionsFilesHook = useOperatorInteractionsFilesSelect;
        const operatorInteractionsGetewaysHook = useOperatorInteractionsGetewaysSelect;
        const operatorsSelectHook = useOperatorsSelect;
        const packageSelectHook = usePackagesSelect;
        const paymentTypeSelectHook = usePaymentTypesSelect;
        const sessionHook = useDiameterSessionsSelect;
        const setsSelectHook = useCardSetsSelect;
        const userHook = useUsersSelectWithTotal;
        const cardHook = useCardsSelectWithTotal;
        const tariffHook = useTariffsSelectWithTotal;
        const tariffPackageHook = useTariffPackagesSelectWithTotal;
        const zoneHook = useZonesSelect;
        const onChangeData = (date?: dayjs.Dayjs): void => {
            setUiDate(date);
            onChange();
        };

        const onChangeDataRange = (dateRange: IDateRange): void => {
            setUiDateRange(dateRange);
            onChange();
        };
        const onChangePageSize = (): void => {
            onChange();
        };
        const onChangeSearchInput = (search: IFilterSelect): void => {
            setUiSearch(search);
            onChange();
        };
        const onChangeCompany = (id: IFilterSelectComposed): void => {
            setUiCompanyId(id);
            onChange();
        };
        const onChangeSeller = (id: IFilterSelectComposed): void => {
            setUiSellerId(id);
            onChange();
        };
        const onChangeBuyer = (id: IFilterSelectComposed): void => {
            setUiBuyerId(id);
            onChange();
        };
        const onChangeCompanyType = (id: IFilterSelectComposed): void => {
            setUiCompanyType(id);
            onChange();
        };
        const onChangePaymentType = (paymentType: IFilterSelectComposed): void => {
            setUiPaymentType(paymentType);
            onChange();
        };
        const onChangePackage = (id: IFilterSelectComposed): void => {
            setUiPackageId(id);
            onChange();
        };
        const onChangeOperator = (id: IFilterSelectComposed): void => {
            setUiOperatorId(id);
            onChange();
        };
        const onChangeCardSet = (id: IFilterSelectComposed): void => {
            setUiCardSetId(id);
            onChange();
        };
        const onChangeSession = (id: IFilterSelectComposed): void => {
            setUiSessionId(id);
            onChange();
        };
        const onChangeUser = (id: IFilterSelectComposed): void => {
            setUiUserId(id);
            onChange();
        };
        const onChangeCard = (id: IFilterSelectComposed): void => {
            setUiCardId(id);
            onChange();
        };
        const onChangeTariff = (id: IFilterSelectComposed): void => {
            setUiTariffId(id);
            onChange();
        };
        const onChangeTariffPackage = (id: IFilterSelectComposed): void => {
            setUiTariffPackageId(id);
            onChange();
        };
        const onChangeEntity = (id: IFilterSelectComposed): void => {
            setUiEntityId(id);
            onChange();
        };
        const onChangeUnitTypes = (ids: IFilterSelectComposed): void => {
            setUiUnitTypes(ids);
            onChange();
        };
        const onChangeGatewayId = (id: IFilterSelectComposed): void => {
            setUiGatewayId(id);
            onChange();
        };
        const onChangeCommandId = (id: IFilterSelectComposed): void => {
            setUiCommandId(id);
            onChange();
        };
        const onChangeFile = (id: IFilterSelectComposed): void => {
            setUiFileId(id);
            onChange();
        };
        const onChangeZone = (id: IFilterSelectComposed): void => {
            setUiZoneId(id);
            onChange();
        };
        const onChangeStatus = (id: IFilterSelectComposed): void => {
            setUiStatus(id);
            onChange();
        };

        const onChangeImportType = (id: IFilterSelectComposed): void => {
            setUiImportType(id);
            onChange();
        };

        // const onChangeSource = (val: IFilterSelectComposed): void => {
        //     setUiSource(val);
        //     onChange();
        // };
        const onChangeIsDelete = (isDelete: boolean): void => {
            setUiIsDelete(isDelete);
            onChange();
        };
        const onChangeWithDeleted = (withDeleted: boolean): void => {
            setUiWithDeleted(withDeleted);
            onChange();
        };

        const onChangeIsActiveThingMonth = (isActiveThisMonth: boolean): void => {
            setUiIsActiveThisMonth(isActiveThisMonth);
            onChange();
        };
        const onChangeIsEimProvided = (isEimProvided: boolean): void => {
            setUiIsEimProvided(isEimProvided);
            onChange();
        };

        const itemColOptions = cardLayoutOptions.filterToolBarFieldCol;
        const itemShortColOptions = cardLayoutOptions.filterToolBarFieldShortCol;
        const itemPageSizeColOptions = cardLayoutOptions.filterToolBarFieldPageSizeCol;

        return (
            <Catcher>
                <div className={cx(className, Styles.filterToolbarPlace, 'formStyle')} {...props}>
                    <Row
                        className={Styles.filterToolbar}
                        justify={'space-between'}
                        gutter={rowGutter.filterToolBarFieldCol}
                        data-test="FilterToolbar"
                    >
                        {filterParams?.pageSize ? (
                            <Col className={Styles.col} {...itemPageSizeColOptions} data-test="PageSize">
                                <FilterPageSize onChange={onChangePageSize} />
                            </Col>
                        ) : null}
                        {filterParams?.unitTypes ? (
                            <Col className={Styles.col} {...itemColOptions} data-test="UnitType">
                                <FilterOriginalUnitTypes filterParams={filterParams} onChange={onChangeUnitTypes} />
                            </Col>
                        ) : null}
                        {filterParams?.dateMonth ? (
                            <Col className={Styles.col} {...itemColOptions} data-test="DataMonth">
                                <FilterDateMonth onChange={onChangeData} filterParams={filterParams} />
                            </Col>
                        ) : null}
                        {filterParams?.dateRange ? (
                            <Col className={Styles.col} {...itemColOptions} data-test="DataRange">
                                <FilterDateRange
                                    onChange={onChangeDataRange}
                                    dateRange={dateRange}
                                    maximumMonths={months}
                                    filterParams={filterParams}
                                />
                            </Col>
                        ) : null}
                        {filterParams?.companyId ? (
                            <Col className={Styles.col} {...itemColOptions} data-test="FilterCompany">
                                <FilterSelect
                                    idSpinners={['GET_COMPANIES_SELECT', 'GET_BILLING_SYSTEM_COMPANIES_SELECT']}
                                    isBlockAllowClear={filterParams.isCompanyIdBlockClear}
                                    onChange={onChangeCompany}
                                    title={tI('Company Filter')}
                                    useSelectHook={companiesSelectHook}
                                    value={companyId}
                                />
                            </Col>
                        ) : null}
                        {filterParams?.sellerId ? (
                            <Col className={Styles.col} {...itemColOptions} data-test="FilterSeller">
                                <FilterSelect
                                    idSpinners={['GET_COMPANIES_SELECT', 'GET_BILLING_SYSTEM_COMPANIES_SELECT']}
                                    isBlockAllowClear={filterParams.isCompanyIdBlockClear}
                                    onChange={onChangeSeller}
                                    title={tI('Seller Filter')}
                                    useSelectHook={companiesSelectHook}
                                    value={sellerId}
                                />
                            </Col>
                        ) : null}
                        {filterParams?.buyerId ? (
                            <Col className={Styles.col} {...itemColOptions} data-test="FilterBuyer">
                                <FilterSelect
                                    idSpinners={['GET_COMPANIES_SELECT', 'GET_BILLING_SYSTEM_COMPANIES_SELECT']}
                                    isBlockAllowClear={filterParams.isCompanyIdBlockClear}
                                    onChange={onChangeBuyer}
                                    title={tI('Buyer Filter')}
                                    useSelectHook={companiesSelectHook}
                                    value={buyerId}
                                />
                            </Col>
                        ) : null}
                        {filterParams?.packageId ? (
                            <Col className={Styles.col} {...itemColOptions} data-test="FilterPackage">
                                <FilterSelect
                                    idSpinners={['GET_PACKAGES_TO_BUY']}
                                    isBlockAllowClear={filterParams.isPackageIdBlockClear}
                                    onChange={onChangePackage}
                                    title={tI('Package Filter')}
                                    useSelectHook={packageSelectHook}
                                    value={packageId}
                                />
                            </Col>
                        ) : null}
                        {filterParams?.companyTypeId ? (
                            <Col className={Styles.col} {...itemColOptions} data-test="FilterCompanyType">
                                <FilterSelect
                                    isBlockAllowClear={filterParams.isCompanyTypeIdBlockClear}
                                    onChange={onChangeCompanyType}
                                    title={tI('Company Type')}
                                    useSelectHook={companyTypeSelectHook}
                                    value={companyTypeId}
                                />
                            </Col>
                        ) : null}
                        {filterParams?.paymentType ? (
                            <Col className={Styles.col} {...itemColOptions} data-test="FilterPaymentType">
                                <FilterSelect
                                    isBlockAllowClear={filterParams.isPaymentTypeBlockClear}
                                    onChange={onChangePaymentType}
                                    title={tI('Payment Filter')}
                                    useSelectHook={paymentTypeSelectHook}
                                    value={paymentType}
                                />
                            </Col>
                        ) : null}
                        {filterParams?.operatorId && isLord ? (
                            <Col className={Styles.col} {...itemColOptions} data-test="FilterOperator">
                                <FilterSelect
                                    idSpinners={['GET_OPERATORS_SELECT']}
                                    isBlockAllowClear={filterParams.isOperatorIdBlockClear}
                                    onChange={onChangeOperator}
                                    title={tI('Operator Filter')}
                                    useSelectHook={operatorsSelectHook}
                                    value={operatorId}
                                />
                            </Col>
                        ) : null}
                        {filterParams?.cardSetId ? (
                            <Col className={Styles.col} {...itemColOptions} data-test="FilterSet">
                                <FilterSelect
                                    idSpinners={['GET_CARD_SETS_SELECT']}
                                    isBlockAllowClear={filterParams.isCardSetIdBlockClear}
                                    onChange={onChangeCardSet}
                                    title={tI('Card Set Filter')}
                                    useSelectHook={setsSelectHook}
                                    value={cardSetId}
                                    virtual={true}
                                />
                            </Col>
                        ) : null}
                        {filterParams?.sessionId ? (
                            <Col className={Styles.col} {...itemColOptions} data-test="FilterSession">
                                <FilterSelect
                                    idSpinners={['GET_DIAMETER_SESSIONS_LIST']}
                                    isBlockAllowClear={filterParams.isSessionIdBlockClear}
                                    onChange={onChangeSession}
                                    title={tI('Session Filter')}
                                    useSelectHook={sessionHook}
                                    value={sessionId}
                                />
                            </Col>
                        ) : null}
                        {filterParams?.zoneId ? (
                            <Col className={Styles.col} {...itemColOptions} data-test="FilterZone">
                                <FilterSelect
                                    idSpinners={['GET_ZONE_SELECT']}
                                    isBlockAllowClear={filterParams.isZoneIdBlockClear}
                                    onChange={onChangeZone}
                                    title={tI('Zone Filter')}
                                    useSelectHook={zoneHook}
                                    value={zoneId}
                                />
                            </Col>
                        ) : null}
                        {filterParams?.userId ? (
                            <Col className={Styles.col} {...itemColOptions} data-test="FilterUser">
                                <FilterLazyLoadingSelect
                                    idSpinners={['GET_USERS_LIST']}
                                    isBlockAllowClear={filterParams.isUserIdBlockClear}
                                    onChange={onChangeUser}
                                    useSelectHook={userHook}
                                    title={tI('User Filter')}
                                    value={userId}
                                    fetchFunc={async (props) => getUsersSelect({ isActivityLogUsers: false, ...props })}
                                />
                            </Col>
                        ) : null}
                        {filterParams?.cardId ? (
                            <Col className={Styles.col} {...itemColOptions} data-test="FilterCard">
                                <FilterLazyLoadingSelect
                                    idSpinners={['GET_CARD_LIST']}
                                    onChange={onChangeCard}
                                    useSelectHook={cardHook}
                                    title={tI('Card Filter')}
                                    value={cardId}
                                    fetchFunc={async (props) => getCardsSelect({ ...props })}
                                />
                            </Col>
                        ) : null}
                        {filterParams?.tariffId ? (
                            <Col className={Styles.col} {...itemColOptions} data-test="FilterTariff">
                                <FilterLazyLoadingSelect
                                    idSpinners={['GET_TARIFFS']}
                                    onChange={onChangeTariff}
                                    useSelectHook={tariffHook}
                                    title={tI('Tariff Filter')}
                                    value={tariffId}
                                    fetchFunc={async (props) => getTariffsSelect({ ...props })}
                                />
                            </Col>
                        ) : null}
                        {filterParams?.tariffPackageId ? (
                            <Col className={Styles.col} {...itemColOptions} data-test="FilterTariffPackage">
                                <FilterLazyLoadingSelect
                                    idSpinners={['GET_TARIFF_PACKAGES']}
                                    onChange={onChangeTariffPackage}
                                    title={tI('Tariff Package Filter')}
                                    useSelectHook={tariffPackageHook}
                                    value={tariffPackageId}
                                    fetchFunc={async (props) => getTariffsPackageSelect({ ...props })}
                                />
                            </Col>
                        ) : null}
                        {filterParams?.entityId ? (
                            <Col className={Styles.col} {...itemColOptions} data-test="FilterEntity">
                                <FilterSelect
                                    idSpinners={['GET_ENTITY_SELECT']}
                                    onChange={onChangeEntity}
                                    title={tI('Entity Filter')}
                                    useSelectHook={entityHook}
                                    value={entityId}
                                />
                            </Col>
                        ) : null}
                        {filterParams?.fileId ? (
                            <Col className={Styles.col} {...itemColOptions} data-test="FilterEntity">
                                <FilterSelect
                                    idSpinners={['GET_OPERATOR_INTERACTIONS_FILES_SELECT']}
                                    onChange={onChangeFile}
                                    title={tI('File Filter')}
                                    useSelectHook={operatorInteractionsFilesHook}
                                    value={operatorInteractionsUploadFileId}
                                />
                            </Col>
                        ) : null}
                        {filterParams?.gatewayId ? (
                            <Col className={Styles.col} {...itemColOptions} data-test="FilterEntity">
                                <FilterSelect
                                    idSpinners={['GET_OPERATOR_INTERACTIONS_GATEWAYS_SELECT']}
                                    onChange={onChangeGatewayId}
                                    title={tI('Gateway Filter')}
                                    useSelectHook={operatorInteractionsGetewaysHook}
                                    value={operatorInteractionsGetewayId}
                                />
                            </Col>
                        ) : null}
                        {filterParams?.commandId ? (
                            <Col className={Styles.col} {...itemColOptions} data-test="FilterEntity">
                                <FilterSelect
                                    idSpinners={['GET_OPERATOR_INTERACTIONS_COMMANDS_SELECT']}
                                    onChange={onChangeCommandId}
                                    title={tI('Command Filter')}
                                    useSelectHook={operatorInteractionsCommandsHook}
                                    value={operatorInteractionsCommandId}
                                />
                            </Col>
                        ) : null}
                        {filterParams?.status ? (
                            <Col className={Styles.col} {...itemColOptions} data-test="FilterEntity">
                                <FilterSelect
                                    onChange={onChangeStatus}
                                    title={tI('Status Filter')}
                                    useSelectHook={statusHook}
                                    value={status}
                                />
                            </Col>
                        ) : null}
                        {filterParams?.importType ? (
                            <Col className={Styles.col} {...itemColOptions} data-test="FilterEntity">
                                <FilterSelect
                                    idSpinners={[]}
                                    onChange={onChangeImportType}
                                    title={tI('Type Filter')}
                                    useSelectHook={ratesImportTypesHook}
                                    value={importType}
                                />
                            </Col>
                        ) : null}
                        {/*{filterParams?.source ? (*/}
                        {/*    <Col className={Styles.col} {...itemColOptions} data-test="FilterSource">*/}
                        {/*        <FilterSelect*/}
                        {/*            idSpinners={[]}*/}
                        {/*            mode="multiple"*/}
                        {/*            onChange={onChangeSource}*/}
                        {/*            title={tI('Filter Unit Type')}*/}
                        {/*            useSelectHook={unitTypesHook}*/}
                        {/*            value={source}*/}
                        {/*        />*/}
                        {/*    </Col>*/}
                        {/*) : null}*/}
                        {filterParams?.search ? (
                            <Col className={Styles.col} {...itemColOptions} data-test="Search">
                                <FilterSearch value={search} onSearch={onChangeSearchInput} />
                            </Col>
                        ) : null}
                        {typeof filterParams?.isDelete !== 'undefined' ? (
                            <Col className={Styles.col} {...itemColOptions} data-test="OnlyDeleted">
                                <div className={cx(Styles.labelFieldPrefix, Styles.labelFieldPrefixDeletedAt)}>
                                    {tF('Show Only Deleted')}
                                </div>
                                <div>
                                    <Checkbox
                                        checked={isDelete}
                                        disabled={false}
                                        onChange={(e: CheckboxChangeEvent): void => onChangeIsDelete(e.target.checked)}
                                    />
                                </div>
                            </Col>
                        ) : null}
                        {typeof filterParams?.withDeleted !== 'undefined' ? (
                            <Col className={Styles.col} {...itemColOptions} data-test="ShowDeleted">
                                <div className={cx(Styles.labelFieldPrefix)}>{tF('Show Deleted')}</div>
                                <div>
                                    <Checkbox
                                        checked={withDeleted}
                                        disabled={false}
                                        onChange={(e: CheckboxChangeEvent): void =>
                                            onChangeWithDeleted(e.target.checked)
                                        }
                                    />
                                </div>
                            </Col>
                        ) : null}
                        {typeof filterParams?.isActiveThisMonth !== 'undefined' ? (
                            <Col className={Styles.col} {...itemShortColOptions} data-test="ActiveThisMonth">
                                <div className={cx(Styles.labelFieldPrefix)}>{tF('Active')}</div>
                                <div>
                                    <Checkbox
                                        checked={isActiveThisMonth}
                                        disabled={false}
                                        onChange={(e: CheckboxChangeEvent): void =>
                                            onChangeIsActiveThingMonth(e.target.checked)
                                        }
                                    />
                                </div>
                            </Col>
                        ) : null}
                        {typeof filterParams?.isEimProvided !== 'undefined' && isLord ? (
                            <Col className={Styles.col} {...itemShortColOptions} data-test="EimProvided">
                                <div className={cx(Styles.labelFieldPrefix)}>{tF('EIM Provided')}</div>
                                <div>
                                    <Checkbox
                                        checked={isEimProvided}
                                        disabled={false}
                                        onChange={(e: CheckboxChangeEvent): void =>
                                            onChangeIsEimProvided(e.target.checked)
                                        }
                                    />
                                </div>
                            </Col>
                        ) : null}
                        {isReloadButton ? <ReloadButton onChange={onChange} idSpinners={idSpinners} /> : null}
                    </Row>
                    {BottomBar ? <BottomBar /> : null}
                </div>
            </Catcher>
        );
    },
);
